'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Button } from '../../components/index.js'
import { Text } from '../../components/index.js'
import { Heading } from '../../components/index.js'
import type { Theme, Img } from '../../types/props.js'
import CircleProgress from './CircleProgress.js'
import { useExposure } from '../../hooks/useExposure.js'

const componentType = 'cart'
const componentName = 'mini_cart'

export type MiniCartSemanticName =
  | 'root'
  | 'content'
  | 'cartInfo'
  | 'cartTitle'
  | 'cartDescription'
  | 'priceSection'
  | 'totalPrice'
  | 'originalPrice'
  | 'saveAmount'
  | 'actionButton'
  | 'itemsGrid'
  | 'itemGridContainer'
  | 'gridItem'
  | 'gridItemOverlay'
  | 'expandButton'
  | 'circleProgress'
  | 'mobileViewMoreButton'

/**
 * 圆形进度条配置
 */
export interface CircleProgressConfig {
  /** 总阶段数 (1-4) */
  totalSteps?: 1 | 2 | 3 | 4
  /** 当前完成的阶段 (0 到 totalSteps) */
  currentStep?: number
  /** 中间显示的图片 */
  image?: Img
  /** 底部显示的文案 */
  label?: string
  /** 进度条颜色，默认为品牌色 */
  progressColor?: string
  /** 底部文案背景色，默认为品牌营销色 */
  labelColor?: string
  /** 背景颜色，默认为容器背景色 */
  backgroundColor?: string
  /** 移动端组件尺寸，默认 48 */
  size?: number
  /** laptop 以上组件尺寸，默认 80 */
  laptopSize?: number
}

/**
 * 文案配置
 */
export interface MiniCartCopy {
  emptyCart: string
  description: string
  total: string
  itemsInCart: string
  totalWithCoupon: string
  clickToView: string
  buyNowText: string
  savingText: string
}

/**
 * MiniCart 业务组件数据接口
 */
export interface MiniCartData {
  /** 文案配置 */
  copy?: MiniCartCopy
  theme?: 'light' | 'dark'
  /** 地区代码，用于价格格式化 */
  locale?: string
  /** 是否显示手机端查看更多按钮，默认 true */
  showMobileViewMore?: boolean
}

/**
 * 购物车商品项类型
 */
export type CartLineItem = {
  id: string
  quantity: number
  /** 商品标签，如 "Free"、"Gift" 等，显示在商品图片底部 */
  productLabel?: string
  cost: {
    totalAmount: {
      amount: string
      currencyCode: string
      formattedPrice: string
    }
    subtotalAmount?: {
      amount: string
      currencyCode: string
      formattedPrice?: string
    }
  }
  merchandise: {
    id: string
    sku: string
    /** 商品标题 - MiniCartDialog 需要 */
    title?: string
    image: {
      url: string
      altText: string | null
    }
  }
}

/**
 * 购物车类型
 */
export type Cart = {
  id: string
  checkoutUrl: string
  lineItems: CartLineItem[]
  cost: {
    totalAmount: {
      amount: string
      currencyCode: string
      formattedPrice: string
    }
    subtotalAmount?: {
      amount: string
      currencyCode: string
      formattedPrice?: string
    }
    savingAmount?: {
      amount?: string
      currencyCode: string
      formattedPrice?: string
    }
  }
}

export interface MiniCartProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 业务数据 */
  data: MiniCartData
  /**
   * 各部分的自定义样式类名
   */
  classNames?: Partial<Record<MiniCartSemanticName, string>>
  /**
   * 产品信息（用于主要展示的产品）
   */
  cart: Cart
  /**
   * 圆形进度条配置，不传则不显示进度条
   */
  progressConfig?: CircleProgressConfig
  /**
   * 删除商品项的回调函数
   * @param id 商品 ID
   * @param cart 购物车数据
   * @returns 支持返回 Promise，内部自动处理 loading 状态
   */
  onRemoveItem?: (id: string, cart: Cart) => void | Promise<void>
  /**
   * 点击结算按钮的回调函数
   * @param cart
   * @returns 支持返回 Promise，内部自动处理 loading 状态
   */
  onCheckout?: (cart: Cart) => void | Promise<void>
  /**
   * 点击click to view的回调函数
   * @param cart
   * @returns
   */
  onClickToView?: (cart: Cart) => void
}

/**
 * MiniCart - 迷你购物车
 *
 * @description 迷你购物车（Mini Cart）是一个常见的电商UI组件，通常在用户浏览商品时以底部悬浮的形式出现，允许用户快速查看、编辑购物车内容而不离开当前页面，同时可以承载满赠，满减等活动。
 */
const MiniCart = React.forwardRef<HTMLDivElement, MiniCartProps>(
  (
    { className, classNames = {}, data, cart, progressConfig, onRemoveItem, onCheckout, onClickToView, ...props },
    ref
  ) => {
    const boxRef = React.useRef<HTMLDivElement>(null)
    const { copy, theme = 'light', locale = 'us', showMobileViewMore = true } = data || {}

    useExposure(boxRef, {
      componentType,
      componentName,
      componentTitle: copy?.itemsInCart || 'Mini Cart',
      componentDescription: copy?.description,
    })

    React.useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

    // 文案配置，带默认值
    const emptyCart = copy?.emptyCart || 'Your Cart is Empty'
    const description = copy?.description || ''
    const itemsInCart = copy?.itemsInCart || 'You have selected {count} items'
    const total = copy?.total || 'Total {total}'
    const totalWithCoupon = copy?.totalWithCoupon || 'total: {total} ({subtotal})'
    const clickToView = copy?.clickToView || 'Click to view cart'
    const buyNowText = copy?.buyNowText || 'Buy Now'
    const savingText = copy?.savingText || 'Saving {amount}'

    // 从Cart对象中提取数据，使用可选链和默认值
    const items = React.useMemo(() => cart?.lineItems || [], [cart?.lineItems])
    const itemCount = items.reduce((total, item) => total + (item?.quantity || 0), 0)
    const isEmpty = itemCount === 0

    // 提前赋值所有金额相关变量
    const totalAmount = parseFloat(cart?.cost?.totalAmount?.amount || '0')
    const subtotalAmount = parseFloat(cart?.cost?.subtotalAmount?.amount || '0')
    const savingAmount = parseFloat(cart?.cost?.savingAmount?.amount || '0')

    // 计算价格信息
    const finalPrice = cart?.cost?.totalAmount?.formattedPrice || ''
    const originalPrice = subtotalAmount > totalAmount ? cart?.cost?.subtotalAmount?.formattedPrice : null
    const savingPrice = savingAmount > 0 ? cart?.cost?.savingAmount?.formattedPrice : null

    // 滚动容器引用
    const scrollContainerRef = React.useRef<HTMLDivElement>(null)

    // 移动端产品展开状态
    const [isProductsExpanded, setIsProductsExpanded] = React.useState(false)

    // checkout loading 状态
    const [checkoutLoading, setCheckoutLoading] = React.useState(false)

    // 删除商品 loading 状态
    const [removingItemId, setRemovingItemId] = React.useState<string | null>(null)

    // 处理删除商品
    const handleRemoveItem = async (id: string) => {
      if (removingItemId) return
      setRemovingItemId(id)
      try {
        await onRemoveItem?.(id, cart)
      } finally {
        setRemovingItemId(null)
      }
    }

    // 鼠标拖拽滚动状态
    const [isDragging, setIsDragging] = React.useState(false)
    const [startX, setStartX] = React.useState(0)
    const [scrollLeft, setScrollLeft] = React.useState(0)

    // 滚动位置状态（控制左右按钮显示）
    const [canScrollLeft, setCanScrollLeft] = React.useState(false)
    const [canScrollRight, setCanScrollRight] = React.useState(true)

    // 检查滚动位置，更新按钮显示状态
    const updateScrollButtons = React.useCallback(() => {
      const container = scrollContainerRef.current
      if (!container) return

      const { scrollLeft, scrollWidth, clientWidth } = container
      setCanScrollLeft(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
    }, [])

    // 监听滚动事件
    const handleScroll = React.useCallback(() => {
      updateScrollButtons()
    }, [updateScrollButtons])

    // 初始化和 items 变化时更新滚动状态
    React.useEffect(() => {
      updateScrollButtons()
    }, [items.length, updateScrollButtons])

    // 鼠标拖拽滚动处理
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!scrollContainerRef.current) return
      setIsDragging(true)
      setStartX(e.pageX - scrollContainerRef.current.offsetLeft)
      setScrollLeft(scrollContainerRef.current.scrollLeft)
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDragging || !scrollContainerRef.current) return
      e.preventDefault()
      const x = e.pageX - scrollContainerRef.current.offsetLeft
      const walk = (x - startX) * 1.5 // 滚动速度倍数
      scrollContainerRef.current.scrollLeft = scrollLeft - walk
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    const handleMouseLeave = () => {
      setIsDragging(false)
    }

    // 水平滚动函数
    const scrollHorizontally = (direction: 'left' | 'right') => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current
        const scrollAmount = 100 // 每次滚动的像素数（调小一些）
        container.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' })
      }
    }

    // 箭头图标组件
    const ArrowIcon = ({ className }: { className?: string }) => (
      <svg
        className={className}
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6 4L10 8L6 12"
          stroke="currentColor"
          strokeWidth="1.33"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )

    // 删除图标组件
    const DeleteIcon = ({ className }: { className?: string }) => (
      <svg
        className={className}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.5 5H17.5M8.33333 9.16667V14.1667M11.6667 9.16667V14.1667M3.33333 5L4.16667 16.6667C4.16667 17.1087 4.34226 17.5326 4.65482 17.8452C4.96738 18.1577 5.39131 18.3333 5.83333 18.3333H14.1667C14.6087 18.3333 15.0326 18.1577 15.3452 17.8452C15.6577 17.5326 15.8333 17.1087 15.8333 16.6667L16.6667 5M7.5 5V2.5C7.5 2.27899 7.5878 2.06702 7.74408 1.91074C7.90036 1.75446 8.11232 1.66667 8.33333 1.66667H11.6667C11.8877 1.66667 12.0996 1.75446 12.2559 1.91074C12.4122 2.06702 12.5 2.27899 12.5 2.5V5"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )

    // Render items grid
    const renderItemsGrid = (theme: Theme) => {
      if (!items.length) return null

      return (
        <div className={cn('laptop:bg-container-secondary-0 flex gap-1 rounded-lg p-1', classNames?.itemsGrid)}>
          {/* 左滚动按钮 - 桌面端显示，少于5个商品时不渲染，滚动到开头时隐藏但保持占位 */}
          {items.length >= 5 && (
            <button
              onClick={() => scrollHorizontally('left')}
              className={cn(
                'border-border laptop:flex hover:bg-muted-foreground/10 hidden w-[32px] shrink-0 items-center justify-center rounded-lg px-2 transition-colors',
                !canScrollLeft &&
                  'laptop:bg-container-secondary-1 laptop:text-transparent laptop:pointer-events-none laptop:-my-1 laptop:-ml-1 laptop:py-1 laptop:pl-1 laptop:rounded-r-none',
                classNames?.expandButton,
                {
                  'laptop:bg-container-secondary-0': theme === 'dark',
                  'laptop:bg-container-primary': theme === 'dark' && !canScrollLeft,
                }
              )}
              aria-label="Scroll left to see more items"
            >
              <ArrowIcon className="laptop:size-4 size-5 rotate-180" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onScroll={handleScroll}
            className={cn(
              'scrollbar-hide laptop:gap-1 laptop:w-[256px] laptop:shrink-0 flex w-full gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
              isDragging ? 'cursor-grabbing' : 'cursor-grab',
              classNames?.itemGridContainer
            )}
          >
            {items
              .filter(line => line && line.id)
              .map(line => {
                const safeProduct = line.merchandise || {}
                const safeImage = safeProduct.image || {}
                const imageUrl = safeImage.url
                const imageAlt = safeImage.altText || 'Product image'
                const isLoading = removingItemId === line.id

                return (
                  <div
                    key={line.id}
                    className={cn('laptop:size-12 group relative size-16 shrink-0', classNames?.gridItem)}
                  >
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt={imageAlt}
                        draggable={false}
                        className="pointer-events-none size-full select-none rounded-lg object-cover"
                      />
                    )}
                    {/* 删除 loading 遮罩 */}
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="animate-spin text-white"
                        >
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.3" />
                          <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                    )}
                    <span
                      role="button"
                      onClick={() => handleRemoveItem(line.id)}
                      className={cn(
                        'laptop:hidden absolute right-[2px] top-[2px] cursor-pointer',
                        isLoading && 'pointer-events-none opacity-0'
                      )}
                      aria-label="delete"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="pointer-events-none"
                      >
                        <rect x="1.33301" y="1.33301" width="13.3333" height="13.3333" rx="6.66667" fill="white" />
                        <path
                          d="M5.37705 5.28896C5.57234 5.09402 5.88839 5.09442 6.08347 5.28965L8.08602 7.29358L10.0906 5.28896C10.2858 5.09402 10.6026 5.09395 10.7977 5.28896C10.9928 5.48418 10.9926 5.80078 10.7977 5.99607L8.79312 8.00069L10.7964 10.0053C10.9913 10.2007 10.9909 10.5179 10.7957 10.7131C10.6005 10.908 10.2844 10.9074 10.0893 10.7124L8.08602 8.7078L6.08347 10.7103C5.88821 10.9056 5.57155 10.9057 5.37636 10.7103C5.18148 10.515 5.18132 10.1984 5.37636 10.0032L7.37891 8.00069L5.37636 5.99676C5.18119 5.80143 5.18181 5.48423 5.37705 5.28896Z"
                          fill="#767880"
                        />
                      </svg>
                    </span>
                    {/* 商品标签 (如 Free Gift) */}
                    {line.productLabel && (
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center">
                        <div
                          className="relative flex w-full items-center justify-center rounded-b-lg  px-1 py-px backdrop-blur-sm"
                          style={{
                            backgroundColor: 'rgba(251, 148, 63, 0.60)',
                          }}
                        >
                          <Text size={1} className="relative text-center text-white" html={line.productLabel} />
                        </div>
                      </div>
                    )}
                    {line.quantity > 1 && !line.productLabel && (
                      <div className="mini-cart-quantity bg-brand-0 laptop:size-4 absolute bottom-0 right-0 flex size-4  items-center justify-center rounded-full text-white">
                        <Text className="text-sm" html={String(line.quantity)} />
                      </div>
                    )}

                    <div
                      role="button"
                      onClick={() => handleRemoveItem(line.id)}
                      className={cn(
                        'absolute inset-0 hidden cursor-pointer items-center justify-center rounded-lg bg-black/60 transition-opacity',
                        'laptop:flex laptop:opacity-0 laptop:group-hover:opacity-100',
                        isLoading && 'laptop:hidden',
                        classNames?.gridItemOverlay
                      )}
                      aria-label="Remove item"
                    >
                      <DeleteIcon className="pointer-events-none size-[20px] text-white" />
                    </div>
                  </div>
                )
              })}

            {/* 手机端查看更多按钮 */}
            {showMobileViewMore && (
              <button
                onClick={() => onClickToView?.(cart)}
                className={cn(
                  'bg-container-secondary-0 laptop:hidden flex size-16 shrink-0 items-center justify-center rounded-md',
                  classNames?.mobileViewMoreButton
                )}
                aria-label={clickToView}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-info-quaternary"
                >
                  <path
                    d="M7.5 5L12.5 10L7.5 15"
                    stroke="currentColor"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* 右滚动按钮 - 桌面端显示，少于5个商品时不渲染，滚动到结尾时隐藏但保持占位 */}
          {items.length >= 5 && (
            <button
              onClick={() => scrollHorizontally('right')}
              className={cn(
                'border-border laptop:flex hover:bg-muted-foreground/10 hidden w-[32px] shrink-0 items-center justify-center rounded-lg px-2 transition-colors',
                !canScrollRight &&
                  'laptop:bg-container-secondary-1 laptop:text-transparent laptop:pointer-events-none laptop:-my-1 laptop:-mr-1 laptop:py-1 laptop:pr-1 laptop:rounded-l-none',
                classNames?.expandButton,
                {
                  'laptop:bg-container-secondary-0': theme === 'dark',
                  'laptop:bg-container-primary': theme === 'dark' && !canScrollRight,
                }
              )}
              aria-label="Scroll right to see more items"
            >
              <ArrowIcon className="laptop:size-4 size-5" />
            </button>
          )}
        </div>
      )
    }

    // Check if cart has items
    const hasItems = !isEmpty && items.length > 0

    return (
      <div
        ref={boxRef}
        className={cn(
          'bg-container-secondary-1 text-info-primary tablet:px-6 laptop:px-12 desktop:px-12 lg-desktop:px-[128px] laptop:py-[16px] flex w-full px-4 py-[12px]',
          // 移动端垂直布局，大屏幕水平布局
          'flex-col items-stretch gap-3',
          'laptop:flex-row laptop:items-center laptop:justify-around',
          hasItems ? 'laptop:items-center laptop:gap-4' : 'laptop:items-center laptop:gap-4',
          'tablet:gap-6 laptop:gap-8',
          className,
          classNames?.root,
          {
            'aiui-dark bg-container-primary': theme === 'dark',
          }
        )}
        {...props}
      >
        {/* 移动端：垂直排列 | 桌面端：水平三列布局 */}
        <div
          className={cn(
            'laptop:flex-row flex w-full flex-col gap-4',
            'laptop:flex-1 laptop:items-center laptop:justify-between',
            'tablet:gap-6 laptop:gap-8',
            classNames?.content
          )}
        >
          <div
            className={cn(
              'relative flex shrink flex-row items-center gap-2',
              'desktop:gap-4',
              progressConfig && 'laptop:pl-[104px] pl-[72px]'
            )}
          >
            {/* 左侧圆形进度条 - 只有配置了才显示 */}
            {progressConfig && (
              <CircleProgress
                totalSteps={progressConfig.totalSteps}
                currentStep={progressConfig.currentStep}
                image={progressConfig.image}
                label={progressConfig.label}
                progressColor={progressConfig.progressColor}
                labelColor={progressConfig.labelColor}
                backgroundColor={progressConfig.backgroundColor}
                size={progressConfig.size}
                laptopSize={progressConfig.laptopSize}
                className={cn(
                  'absolute bottom-0 left-0',
                  'lg-desktop:bottom-auto lg-desktop:top-[-46px] desktop:bottom-auto desktop:top-[-52px]',
                  classNames?.circleProgress
                )}
              />
            )}
            {/* Center: Cart info and dynamic content */}
            <div className="laptop:flex-1 flex w-full flex-col">
              {/* Cart title area */}
              <div className={cn('tablet:gap-2 flex flex-col gap-1', classNames?.cartInfo)}>
                {hasItems ? (
                  <div className="flex w-full items-center gap-2">
                    <Heading
                      size={2}
                      as="h3"
                      className={cn('', classNames?.cartTitle)}
                      html={itemsInCart.replace('{count}', `<span class="text-brand-0">${itemCount || 0}</span>`)}
                    />

                    {/* Click to view link / Toggle products */}
                    <Heading
                      size={1}
                      as="h5"
                      onClick={() => {
                        // 移动端：切换产品展开状态，桌面端：调用原回调
                        const isMobile = window.innerWidth < 1025
                        if (isMobile) {
                          setIsProductsExpanded(!isProductsExpanded)
                        } else {
                          onClickToView?.(cart)
                        }
                      }}
                      className={cn('flex cursor-pointer items-center gap-1', classNames?.expandButton)}
                    >
                      {/* 桌面端显示文字 + 图标，移动端只显示图标 */}
                      <Text
                        as="span"
                        size={1}
                        className="desktop:block hidden text-nowrap text-[16px] "
                        html={clickToView}
                      />
                      <ArrowIcon
                        className={cn(
                          'size-4 transition-transform',
                          isProductsExpanded ? 'laptop:rotate-0 -rotate-90' : 'laptop:rotate-0 rotate-90'
                        )}
                      />
                    </Heading>
                  </div>
                ) : (
                  <Heading
                    as="h3"
                    size={2}
                    className={cn('tablet:text-2xl text-xl font-semibold', classNames?.cartTitle)}
                    html={emptyCart}
                  />
                )}
              </div>

              {description && (
                <Text
                  size={2}
                  className={cn(
                    'mini-cart-description text-info-tertiary desktop:text-[16px] lg-desktop:text-[18px] text-[14px]',
                    classNames?.cartDescription
                  )}
                  html={description}
                />
              )}
            </div>
          </div>

          {/* Items grid (移动端：根据展开状态显示，桌面端：始终显示) */}
          {hasItems && (
            <div className={cn('laptop:block laptop:shrink', isProductsExpanded ? 'block' : 'hidden')}>
              {renderItemsGrid(theme)}
            </div>
          )}
          {/* 移动端：分割线（只在有商品时显示） */}
          {hasItems && <hr className="laptop:hidden border-lines w-full border-t" />}

          {/* Price and button area */}
          <div
            className={cn(
              'tablet:gap-6 laptop:gap-4 laptop:justify-end flex flex-row items-stretch justify-between gap-4',
              classNames?.priceSection
            )}
          >
            {/* Price info */}
            <div className="flex flex-col items-end justify-center">
              {/* Main price row */}
              <div className="flex items-center gap-2">
                <Heading
                  as="h6"
                  size={2}
                  className={cn('whitespace-nowrap text-nowrap', classNames?.totalPrice)}
                  html={total.replace('{total}', finalPrice)}
                />

                {/* Original price (if discounted) */}
                {originalPrice && (
                  <Heading
                    as="h6"
                    size={2}
                    className={cn('text-info-tertiary  whitespace-nowrap line-through', classNames?.originalPrice)}
                    html={originalPrice}
                  />
                )}
              </div>

              {/* Save amount */}
              {savingPrice && (
                <Heading
                  size={2}
                  as="h6"
                  className={cn(
                    'laptop:text-right text-marketing-1 w-full whitespace-nowrap text-nowrap text-left',
                    classNames?.saveAmount
                  )}
                  html={savingText.replace('{amount}', savingPrice)}
                />
              )}
            </div>

            {/* Action button */}
            <Button
              disabled={!hasItems}
              loading={checkoutLoading}
              onClick={async () => {
                if (checkoutLoading) return
                setCheckoutLoading(true)
                try {
                  await onCheckout?.(cart)
                } finally {
                  setCheckoutLoading(false)
                }
              }}
              className={cn('whitespace-nowrap', classNames?.actionButton)}
            >
              {buyNowText}
            </Button>
          </div>
        </div>
      </div>
    )
  }
)

MiniCart.displayName = 'MiniCart'
export default MiniCart
